import { CatalogBghTileItem } from '~/features/catalog/components/CatalogBghTileItem';
import type { BghArticle } from '~/common/types/bghArticle';
import { CatalogAghTileItem } from '~/features/catalog/components/CatalogAghTileItem';
import { Button } from '~/common/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useBasePath } from '~/common/hooks/useBasePath';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { useLoginInfo } from '~/shell/hooks/useLoginInfo';
import type { EmblaCarouselType } from 'embla-carousel';
import type { AghArticle } from '../types/aghArticle';

export function ArticleSlider({ articles, className }: Readonly<{ articles: BghArticle[] | AghArticle[]; className?: string }>) {
  const { isBgh } = useBasePath();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, slidesToScroll: 'auto', align: 'start' });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const { loginInfo } = useLoginInfo();

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  if (!articles) {
    return null;
  }

  const isAuthenticated = !!loginInfo;

  return (
    <div className={className}>
      <div className="overflow-hidden h-fit" ref={emblaRef}>
        <div className="flex divide-x">
          {articles?.map(article => (
            <div key={article.articleNumber} className={'flex-[0_0_260px] min-w-0'}>
              {isBgh ? (
                <CatalogBghTileItem article={article as BghArticle} isAuthenticated={isAuthenticated} />
              ) : (
                <CatalogAghTileItem article={article} isAuthenticated={isAuthenticated} />
              )}
            </div>
          ))}
        </div>
      </div>
      {(!prevBtnDisabled || !nextBtnDisabled) && (
        <div className={'flex gap-2 mt-4'}>
          <Button
            disabled={prevBtnDisabled}
            variant={'ghost'}
            className="border rounded-full p-0 h-[36px] w-[36px]"
            data-testid={'articleslider-left-button'}
            onClick={scrollPrev}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant={'ghost'}
            disabled={nextBtnDisabled}
            className="border rounded-full p-0 h-[36px] w-[36px]"
            data-testid={'articleslider-right-button'}
            onClick={scrollNext}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
}
